import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchGorusmeKayitlari } from '../services/gorusmeKaydiService';
import TableListFooter from '../components/TableListFooter';
import MusteriCagriListesi from '../components/MusteriCagriListesi';
import { PAGE_DEFAULT_LIMIT } from '../constants/pagination';
import { parseApiDate } from '../utils/security';
import './MusteriHizmetleriPage.css';

const DURUM_LABELS = {
  DevamEdiyor: 'Devam ediyor',
  ServisKaydiOlusturuldu: 'Servis kaydı oluşturuldu',
  GorusmeSonlandirildi: 'Görüşme sonlandırıldı',
};

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  const msg = error?.message || error?.data?.mesaj || error?.data?.message || fallback;
  return msg === 'Failed to fetch' ? 'Sunucuya bağlanılamadı' : msg;
};

const formatTarih = (val) => {
  const d = parseApiDate(val);
  if (!d || Number.isNaN(d.getTime())) return val || '—';
  return d.toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });
};

const GorusmeKayitlariPage = () => {
  const navigate = useNavigate();
  const [sekme, setSekme] = useState('gorusmeler');

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [durumFilter, setDurumFilter] = useState('');
  const [kaynakFilter, setKaynakFilter] = useState('');
  const [sayfa, setSayfa] = useState(1);
  const [toplam, setToplam] = useState(0);
  const [toplamSayfa, setToplamSayfa] = useState(1);
  const limit = PAGE_DEFAULT_LIMIT;

  const loadList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchGorusmeKayitlari({
        sayfa,
        limit,
        durum: durumFilter || undefined,
        kaynak: kaynakFilter || undefined,
      });
      if (res?.basarili && res?.veri) {
        setList(res.veri.gorusmeKayitlari || []);
        setToplam(res.veri.toplam ?? 0);
        setToplamSayfa(res.veri.toplamSayfa ?? 1);
      } else {
        setList([]);
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Görüşme kayıtları yüklenemedi'));
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [sayfa, limit, durumFilter, kaynakFilter]);

  useEffect(() => {
    if (sekme === 'gorusmeler') loadList();
  }, [sekme, loadList]);

  return (
    <main className="main-content">
      <div className="content-header">
        <h1>Görüşme Kayıtları</h1>
      </div>

      <div className="mh-tabs" style={{ marginBottom: '20px' }}>
        <button
          type="button"
          className={`mh-tab${sekme === 'gorusmeler' ? ' is-active' : ''}`}
          onClick={() => setSekme('gorusmeler')}
        >
          Görüşmeler
        </button>
        <button
          type="button"
          className={`mh-tab${sekme === 'gelen' ? ' is-active' : ''}`}
          onClick={() => setSekme('gelen')}
        >
          Çağrılar
        </button>
      </div>

      {sekme === 'gorusmeler' ? (
        <>
          <div className="filter-bar" style={{ marginBottom: '16px' }}>
            <label htmlFor="durum-filter" style={{ marginRight: '8px' }}>Durum:</label>
            <select
              id="durum-filter"
              value={durumFilter}
              onChange={(e) => { setDurumFilter(e.target.value); setSayfa(1); }}
              className="filter-select"
              style={{ marginRight: '12px' }}
            >
              <option value="">Tümü</option>
              {Object.entries(DURUM_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            <label htmlFor="kaynak-filter" style={{ marginRight: '8px' }}>Tip:</label>
            <select
              id="kaynak-filter"
              value={kaynakFilter}
              onChange={(e) => { setKaynakFilter(e.target.value); setSayfa(1); }}
              className="filter-select"
            >
              <option value="">Tümü</option>
              <option value="santral">Santral</option>
              <option value="manuel">Manuel</option>
            </select>
          </div>

          {error && (
            <div className="error-state" style={{ marginBottom: '16px' }}>
              <i className="fas fa-exclamation-circle"></i>
              <span>{error}</span>
            </div>
          )}

          {loading ? (
            <div className="loading-state">
              <i className="fas fa-spinner fa-spin"></i>
              <span>Yükleniyor...</span>
            </div>
          ) : list.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-comments"></i>
              <span>Görüşme kaydı bulunamadı.</span>
            </div>
          ) : (
            <>
              <div className="table-container">
                <table className="admins-table">
                  <thead>
                    <tr>
                      <th>Tarih</th>
                      <th>Tip</th>
                      <th>Telefon</th>
                      <th>Müşteri</th>
                      <th>Durum</th>
                      <th>Notlar</th>
                      <th>Servis Kaydı</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((g) => (
                      <tr key={g.id || g._id}>
                        <td className="cell-nowrap">{formatTarih(g.olusturmaTarihi || g.createdAt)}</td>
                        <td>{g.kaynak === 'santral' ? 'Santral' : 'Manuel'}</td>
                        <td>{g.telefon || '—'}</td>
                        <td className="cell-truncate">{g.musteriAdi || '—'}</td>
                        <td>{DURUM_LABELS[g.durum] || g.durum || '—'}</td>
                        <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {g.notlar || '—'}
                        </td>
                        <td>
                          {g.servisKaydiId ? (
                            <Link to={`/service-records/${g.servisKaydiId}/update`}>
                              {g.servisKaydiId}
                            </Link>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <TableListFooter
                totalRecords={toplam}
                currentPage={sayfa}
                totalPages={toplamSayfa || 1}
                onPageChange={setSayfa}
                loading={loading}
              />
            </>
          )}
        </>
      ) : (
        <MusteriCagriListesi tip="gelen" aktif={sekme === 'gelen'} />
      )}
    </main>
  );
};

export default GorusmeKayitlariPage;
